import { useState, useEffect, useCallback } from 'react';
import { CalendarClock, Plus, Pencil, Trash2 } from 'lucide-react';
import api from '../../utils/Api';

const DIAS = [
  { value: 'monday',    label: 'Lun' },
  { value: 'tuesday',   label: 'Mar' },
  { value: 'wednesday', label: 'Mié' },
  { value: 'thursday',  label: 'Jue' },
  { value: 'friday',    label: 'Vie' },
  { value: 'saturday',  label: 'Sáb' },
  { value: 'sunday',    label: 'Dom' },
];

const VACIO = {
  usuario_id: '',
  tipo: 'puntual',
  hora_inicio: '09:00',
  hora_fin: '17:00',
  fecha_puntual: '',
  dias_semana: [],
  fecha_fin_recurrencia: '',
};

function formatDias(dias) {
  if (!dias || !dias.length) return '—';
  return dias.map((d) => DIAS.find((x) => x.value === d)?.label ?? d).join(', ');
}

export default function Horarios() {
  const [schedules, setSchedules]   = useState([]);
  const [empleados, setEmpleados]   = useState([]);
  const [modal, setModal]           = useState(false);
  const [editando, setEditando]     = useState(null);
  const [form, setForm]             = useState(VACIO);
  const [error, setError]           = useState('');
  const [loading, setLoading]       = useState(false);

  const cargarSchedules = useCallback(() => {
    api.get('/schedules').then((r) => setSchedules(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    cargarSchedules();
    api.get('/usuarios').then((r) => setEmpleados(r.data)).catch(() => {});
  }, [cargarSchedules]);

  function abrirNuevo() {
    setForm(VACIO);
    setEditando(null);
    setError('');
    setModal(true);
  }

  function abrirEditar(s) {
    setForm({
      usuario_id:            s.usuario_id,
      tipo:                  s.tipo,
      hora_inicio:           s.hora_inicio?.slice(0, 5) ?? '',
      hora_fin:              s.hora_fin?.slice(0, 5) ?? '',
      fecha_puntual:         s.fecha_puntual ?? '',
      dias_semana:           s.dias_semana ?? [],
      fecha_fin_recurrencia: s.fecha_fin_recurrencia ?? '',
    });
    setEditando(s.id);
    setError('');
    setModal(true);
  }

  function toggleDia(dia) {
    setForm((f) => {
      const dias = f.dias_semana.includes(dia)
        ? f.dias_semana.filter((d) => d !== dia)
        : [...f.dias_semana, dia];
      return { ...f, dias_semana: dias };
    });
  }

  async function guardar(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        usuario_id:  form.usuario_id,
        tipo:        form.tipo,
        hora_inicio: form.hora_inicio,
        hora_fin:    form.hora_fin,
        ...(form.tipo === 'puntual'
          ? { fecha_puntual: form.fecha_puntual }
          : {
              dias_semana: form.dias_semana,
              ...(form.fecha_fin_recurrencia ? { fecha_fin_recurrencia: form.fecha_fin_recurrencia } : {}),
            }),
      };

      if (editando) {
        await api.put(`/schedules/${editando}`, payload);
      } else {
        await api.post('/schedules', payload);
      }

      setModal(false);
      cargarSchedules();
    } catch (err) {
      setError(err.message ?? 'Error al guardar el horario.');
    } finally {
      setLoading(false);
    }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este horario?')) return;
    await api.delete(`/schedules/${id}`);
    cargarSchedules();
  }

  return (
    <div className="container-fluid py-4">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 className="mb-0 d-flex align-items-center gap-2">
          <CalendarClock size={22} /> Horarios
        </h4>
        <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={abrirNuevo}>
          <Plus size={16} /> Nuevo horario
        </button>
      </div>

      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th>Empleado</th>
              <th>Tipo</th>
              <th>Día / Fecha</th>
              <th>Inicio</th>
              <th>Fin</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {schedules.length === 0 && (
              <tr><td colSpan={6} className="text-center text-muted py-4">Sin horarios</td></tr>
            )}
            {schedules.map((s) => {
              const emp = empleados.find((e) => e.id === s.usuario_id);
              return (
                <tr key={s.id}>
                  <td>{emp ? `${emp.nombre} ${emp.apellidos}` : `#${s.usuario_id}`}</td>
                  <td>
                    <span className={`badge ${s.tipo === 'puntual' ? 'bg-info' : 'bg-success'}`}>
                      {s.tipo === 'puntual' ? 'Puntual' : 'Recurrente'}
                    </span>
                  </td>
                  <td>
                    {s.tipo === 'puntual'
                      ? s.fecha_puntual
                      : formatDias(s.dias_semana)}
                  </td>
                  <td>{s.hora_inicio?.slice(0, 5)}</td>
                  <td>{s.hora_fin?.slice(0, 5)}</td>
                  <td>
                    <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => abrirEditar(s)}>
                      <Pencil size={14} />
                    </button>
                    <button className="btn btn-sm btn-outline-danger" onClick={() => eliminar(s.id)}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editando ? 'Editar horario' : 'Nuevo horario'}</h5>
                <button className="btn-close" onClick={() => setModal(false)} />
              </div>
              <form onSubmit={guardar}>
                <div className="modal-body">
                  {error && <div className="alert alert-danger py-2">{error}</div>}

                  <div className="mb-3">
                    <label className="form-label">Empleado</label>
                    <select
                      className="form-select"
                      required
                      value={form.usuario_id}
                      onChange={(e) => setForm((f) => ({ ...f, usuario_id: e.target.value }))}
                    >
                      <option value="">Selecciona empleado</option>
                      {empleados
                        .filter((e) => e.rol === 'empleado')
                        .map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.nombre} {e.apellidos}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Tipo</label>
                    <div className="d-flex gap-3">
                      {['puntual', 'recurrente'].map((t) => (
                        <div key={t} className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            id={`tipo-${t}`}
                            checked={form.tipo === t}
                            onChange={() => setForm((f) => ({ ...f, tipo: t }))}
                          />
                          <label className="form-check-label text-capitalize" htmlFor={`tipo-${t}`}>{t}</label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col">
                      <label className="form-label">Hora inicio</label>
                      <input
                        type="time"
                        className="form-control"
                        required
                        value={form.hora_inicio}
                        onChange={(e) => setForm((f) => ({ ...f, hora_inicio: e.target.value }))}
                      />
                    </div>
                    <div className="col">
                      <label className="form-label">Hora fin</label>
                      <input
                        type="time"
                        className="form-control"
                        required
                        value={form.hora_fin}
                        onChange={(e) => setForm((f) => ({ ...f, hora_fin: e.target.value }))}
                      />
                    </div>
                  </div>

                  {form.tipo === 'puntual' && (
                    <div className="mb-3">
                      <label className="form-label">Fecha</label>
                      <input
                        type="date"
                        className="form-control"
                        required
                        value={form.fecha_puntual}
                        onChange={(e) => setForm((f) => ({ ...f, fecha_puntual: e.target.value }))}
                      />
                    </div>
                  )}

                  {form.tipo === 'recurrente' && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Días de la semana</label>
                        <div className="d-flex flex-wrap gap-2">
                          {DIAS.map((d) => (
                            <button
                              key={d.value}
                              type="button"
                              className={`btn btn-sm ${form.dias_semana.includes(d.value) ? 'btn-primary' : 'btn-outline-secondary'}`}
                              onClick={() => toggleDia(d.value)}
                            >
                              {d.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Fin de recurrencia (opcional)</label>
                        <input
                          type="date"
                          className="form-control"
                          value={form.fecha_fin_recurrencia}
                          onChange={(e) => setForm((f) => ({ ...f, fecha_fin_recurrencia: e.target.value }))}
                        />
                      </div>
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setModal(false)}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={loading}>
                    {loading ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
